import React, { useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

// Renders the official "Sign in with Google" button via Google Identity
// Services. It only appears when REACT_APP_GOOGLE_CLIENT_ID is configured, so
// the app works fine before Google auth is set up.
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

declare global {
  interface Window {
    google?: any;
  }
}

const GoogleSignInButton: React.FC = () => {
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!CLIENT_ID || !divRef.current) return;

    const init = () => {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: async (response: { credential: string }) => {
          try {
            await googleLogin(response.credential);
            navigate('/app/dashboard');
          } catch {
            /* toast already shown by googleLogin */
          }
        },
      });
      window.google.accounts.id.renderButton(divRef.current, {
        theme: 'outline',
        size: 'large',
        width: 320,
        text: 'continue_with',
      });
    };

    // Load the GIS script once, then init.
    if (window.google?.accounts?.id) {
      init();
    } else {
      const existing = document.getElementById('google-gsi');
      if (existing) {
        existing.addEventListener('load', init);
      } else {
        const s = document.createElement('script');
        s.src = 'https://accounts.google.com/gsi/client';
        s.async = true;
        s.defer = true;
        s.id = 'google-gsi';
        s.onload = init;
        document.head.appendChild(s);
      }
    }
  }, [googleLogin, navigate]);

  if (!CLIENT_ID) return null; // hidden until configured

  return (
    <div className="mt-4">
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-white px-2 text-gray-400">or</span>
        </div>
      </div>
      <div ref={divRef} className="flex justify-center" />
    </div>
  );
};

export default GoogleSignInButton;
